import { useEffect } from 'react';
import { HQ } from '../data/company';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Geofleet.ai';
const SITE_URL = 'https://geofleet.ai';
const DEFAULT_TITLE = 'Delivery Management Software with AI Dispatch & Copilot';
const DEFAULT_DESCRIPTION =
  'Geofleet plans optimized routes, dispatches them to your drivers, tracks every stop live and captures proof of delivery, with an AI Copilot. Plans from $49 a month for 3 drivers.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;
/** Shown in og:image:alt / Twitter for accessibility and rich results */
const DEFAULT_OG_IMAGE_ALT =
  'Geofleet command centre with live delivery map, KPIs and today’s orders';
const TWITTER_SITE = '@geofleetai';
/** Match index.html so SPA navigations restore full directive after visiting a noindex page */
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

/** Social profiles for Organization sameAs (verified public URLs) */
export const ORGANIZATION_SAME_AS = [
  'https://www.linkedin.com/company/geofleetai/',
  'https://x.com/geofleetai',
  'https://www.youtube.com/@getgeofleet',
] as const;

interface SEOProps {
  title?: string;
  /** When true, `title` is used as the full document and OG title (no automatic `| Geofleet.ai` suffix). */
  exactDocumentTitle?: boolean;
  description?: string;
  ogImage?: string;
  /** Alt text for og:image and Twitter image (accessibility + social previews) */
  ogImageAlt?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
}

function setMeta(nameOrProperty: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${nameOrProperty}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.href = href;
}

export default function SEO({
  title,
  exactDocumentTitle = false,
  description = DEFAULT_DESCRIPTION,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogType = 'website',
  noIndex = false,
  jsonLd,
  article,
}: SEOProps) {
  const { pathname } = useLocation();
  const canonicalUrl = `${SITE_URL}${pathname === '/' ? '' : pathname}`;
  const fullTitle =
    exactDocumentTitle && title
      ? title
      : title
        ? `${title} | ${SITE_NAME}`
        : `${DEFAULT_TITLE} | ${SITE_NAME}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('description', description);
    setCanonical(canonicalUrl);

    if (noIndex) {
      setMeta('robots', 'noindex,nofollow');
    } else {
      setMeta('robots', DEFAULT_ROBOTS);
    }

    // Open Graph
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:type', ogType, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:image:alt', ogImageAlt, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('og:locale', 'en_US', true);

    // Article-specific OG tags
    if (article?.publishedTime) {
      setMeta('article:published_time', article.publishedTime, true);
    }
    if (article?.modifiedTime) {
      setMeta('article:modified_time', article.modifiedTime, true);
    }
    if (article?.author) {
      setMeta('article:author', article.author, true);
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', TWITTER_SITE);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:image:alt', ogImageAlt);

    // JSON-LD Structured Data
    const jsonLdId = 'seo-jsonld';
    let scriptEl = document.getElementById(jsonLdId) as HTMLScriptElement | null;
    if (jsonLd) {
      const data = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = jsonLdId;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(data);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      // Clean up article-specific tags on unmount
      document.querySelector('meta[property="article:published_time"]')?.remove();
      document.querySelector('meta[property="article:modified_time"]')?.remove();
      document.querySelector('meta[property="article:author"]')?.remove();
    };
  }, [fullTitle, description, canonicalUrl, ogImage, ogImageAlt, ogType, noIndex, jsonLd, article]);

  return null;
}

// Reusable JSON-LD generators
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Geofleet',
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  description: DEFAULT_DESCRIPTION,
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'care@geofleet.ai',
    contactType: 'customer service',
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: HQ.lines.join(', '),
    addressLocality: 'Singapore',
    postalCode: '049422',
    addressCountry: 'SG',
  },
  sameAs: [...ORGANIZATION_SAME_AS],
};

export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  publisher: {
    '@type': 'Organization',
    name: 'Geofleet',
    url: SITE_URL,
  },
};

export function breadcrumbJsonLd(
  items: { name: string; url: string }[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function softwareApplicationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Geofleet',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'AI agents for logistics: delivery management with autonomous dispatch, carrier and driver allocation, agentic route planning, SLA alerts, real-time tracking, and last-mile command centre.',
    url: SITE_URL,
    offers: {
      '@type': 'Offer',
      price: '49',
      priceCurrency: 'USD',
      description: 'Geofleet Lite from $49 per month for 3 drivers and 500 orders; full Geofleet from $19 per driver with a 10-driver minimum.',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Geofleet',
      url: SITE_URL,
    },
  };
}

/**
 * Article / BlogPosting structured data. Use `BlogPosting` for blog posts and `Article`
 * for case studies. `dateModified` falls back to `datePublished` when not supplied.
 */
export function articleJsonLd(opts: {
  type?: 'Article' | 'BlogPosting';
  headline: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  section?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': opts.type ?? 'Article',
    headline: opts.headline,
    description: opts.description,
    url: opts.url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': opts.url },
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.datePublished || opts.dateModified
      ? { dateModified: opts.dateModified ?? opts.datePublished }
      : {}),
    ...(opts.section ? { articleSection: opts.section } : {}),
    author: { '@type': 'Person', name: opts.authorName ?? 'Geofleet' },
    publisher: {
      '@type': 'Organization',
      name: 'Geofleet',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.png` },
    },
  };
}

/** Ranked list (e.g. a "best X" listicle) as schema.org ItemList. */
export function itemListJsonLd(name: string, items: { name: string; url: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
    })),
  };
}

export function faqJsonLd(
  faqs: { question: string; answer: string }[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
